import { computed, type Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type {
  ConnectionTemplateAuthoring,
  ConnectionTemplateAuthoringListItem,
  CreateConnectionTemplateRequest,
  UpdateConnectionTemplateRequest,
} from "@/types";

// Superadmin template-designer hooks (#5759) over the authoring API. Writes also invalidate the
// published-catalogue keys owned by connection-templates.ts: update reverts a template to Draft and
// publish adds it to the picker, so both catalogues change shape on the same actions.

const BASE = `${import.meta.env.VITE_API_URL}/connection-templates`;

// READ — every template (draft + published) for the designer's catalogue.
export function useAuthoringTemplates() {
  const { data, isFetching: templatesLoading, error: templatesError, refetch: refetchTemplates } = useQuery({
    queryKey: ["connection-templates-authoring"],
    queryFn: () => fetchWrapper.get<ConnectionTemplateAuthoringListItem[]>(`${BASE}/authoring`),
  });
  const templates = computed(() => data.value ?? []);
  return { templates, templatesLoading, templatesError: templatesError as Ref<ApiError | null>, refetchTemplates };
}

// READ — one template's full authoring content, any status.
export function useAuthoringTemplate(id: Ref<string>) {
  const { data: template, isFetching: templateLoading, error: templateError, refetch: refetchTemplate } = useQuery({
    queryKey: computed(() => ["connection-template-authoring", id.value]),
    queryFn: () => fetchWrapper.get<ConnectionTemplateAuthoring>(`${BASE}/authoring/${id.value}`),
    enabled: computed(() => !!id.value),
  });
  return { template, templateLoading, templateError: templateError as Ref<ApiError | null>, refetchTemplate };
}

// WRITE — create a draft.
export function useCreateAuthoringTemplate() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<
    ConnectionTemplateAuthoring,
    ApiError,
    CreateConnectionTemplateRequest
  >({
    mutationFn: (data) => fetchWrapper.post<ConnectionTemplateAuthoring>(BASE, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connection-templates-authoring"] }),
  });
  return {
    createTemplate: mutate,
    createTemplateAsync: mutateAsync,
    createTemplatePending: isPending,
    createTemplateError: error as Ref<ApiError | null>,
    createTemplateReset: reset,
  };
}

// WRITE — replace content + display metadata; the server reverts the template to Draft.
export function useUpdateAuthoringTemplate() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<
    ConnectionTemplateAuthoring,
    ApiError,
    { id: string; data: UpdateConnectionTemplateRequest }
  >({
    mutationFn: ({ id, data }) => fetchWrapper.put<ConnectionTemplateAuthoring>(`${BASE}/${id}`, data),
    onSuccess: (dto) => {
      queryClient.invalidateQueries({ queryKey: ["connection-templates-authoring"] });
      queryClient.invalidateQueries({ queryKey: ["connection-template-authoring", dto.id] });
      queryClient.invalidateQueries({ queryKey: ["connection-templates"] });
      queryClient.invalidateQueries({ queryKey: ["connection-template", dto.id] });
    },
  });
  return {
    updateTemplate: mutate,
    updateTemplateAsync: mutateAsync,
    updateTemplatePending: isPending,
    updateTemplateError: error as Ref<ApiError | null>,
    updateTemplateReset: reset,
  };
}

// WRITE — delete a draft no connections reference (the server guards both conditions).
export function useDeleteAuthoringTemplate() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, string>({
    mutationFn: (id) => fetchWrapper.delete<void>(`${BASE}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connection-templates-authoring"] }),
  });
  return {
    deleteTemplate: mutate,
    deleteTemplateAsync: mutateAsync,
    deleteTemplatePending: isPending,
    deleteTemplateError: error as Ref<ApiError | null>,
    deleteTemplateReset: reset,
  };
}

// WRITE — publish a draft so it appears in the New Connection picker.
export function usePublishAuthoringTemplate() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<ConnectionTemplateAuthoring, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post<ConnectionTemplateAuthoring>(`${BASE}/${id}/publish`, {}),
    onSuccess: (dto) => {
      queryClient.invalidateQueries({ queryKey: ["connection-templates-authoring"] });
      queryClient.invalidateQueries({ queryKey: ["connection-template-authoring", dto.id] });
      queryClient.invalidateQueries({ queryKey: ["connection-templates"] });
      queryClient.invalidateQueries({ queryKey: ["connection-template", dto.id] });
    },
  });
  return {
    publishTemplate: mutate,
    publishTemplateAsync: mutateAsync,
    publishTemplatePending: isPending,
    publishTemplateError: error as Ref<ApiError | null>,
    publishTemplateReset: reset,
  };
}
